import React, { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Size = "narrow" | "default" | "wide" | "full";

const SIZE_CLASS: Record<Size, string> = {
  narrow: "max-w-3xl",   // ~768px — prose, FAQ, focused forms
  default: "max-w-5xl",  // ~1024px — most marketing sections
  wide: "max-w-7xl",     // ~1280px — heroes, dense grids, dashboards
  full: "",              // edge-padded only, no cap
};

interface ContainerProps {
  children: ReactNode;
  size?: Size;
  className?: string;
  /** Render as <section>, <div>, etc. Default <div>. */
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Site-wide content container. Standardizes max-width and edge padding
 * across mobile, tablet, desktop, large-desktop, and ultrawide.
 *
 * Padding scales: 16px → 24px → 32px → 40px.
 */
export function Container({
  children,
  size = "default",
  className,
  as: Tag = "div",
}: ContainerProps) {
  const Component = Tag as React.ElementType;
  return (
    <Component
      className={cn(
        "mx-auto w-full px-4 sm:px-6 lg:px-8 xl:px-10",
        SIZE_CLASS[size],
        className
      )}
    >
      {children}
    </Component>
  );
}
